import {Injectable} from "@angular/core";
import {PrintPreviewHelperDialogComponent} from "./print-preview-helper-dialog.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {
  centerPopup, copyDOM, defaultPopupOptions, escapeHtml, PopupOptions,
  stringifyPopupOptions,
} from "../misc/dom-helper";

@Injectable()
export class PrintPreviewService {
  private elementMap: {[key: string]: HTMLElement} = {};

  private showPrintPreviewWindow(
    url?: string,
    target?: string,
    features?: string,
    replace?: boolean,
  ): Promise<Window> {
    let win = window.open(url, target, features, replace);
    if (win) return Promise.resolve(win);

    return new Promise((resolve, reject) => {
      PrintPreviewHelperDialogComponent.show(this.dialogService)
        .subscribe(
          (result) => {
            if (result.retry === true) {
              win = window.open(url, target, features, replace);
              if (win) {
                result.close();
              }
            } else {
              resolve(win);
            }
          },
          (error) => {
            reject(error);
          },
        );
    });
  }

  constructor(private dialogService: DialogService) {}

  register(key: string, element: HTMLElement): void {
    if (!key) return;
    this.elementMap[key] = element;
  }

  deregister(key: string): void {
    if (!key) return;
    if (Object.prototype.hasOwnProperty.call(this.elementMap, key)) {
      delete this.elementMap[key];
    }
  }

  getPrintableByKey(key: string): HTMLElement {
    return this.elementMap[key];
  }

  /**
   * Finds the first sibling or parent HTMLElement that is "printable"
   * Will not look more than 20 layers up
   * @param {HTMLElement} element
   * @returns {HTMLElement}
   */
  getNearbyPrintable(element: HTMLElement): HTMLElement {
    let result: HTMLElement = element;
    for (let i = 0; result && i < 20; i++) {
      if (result.hasAttribute("printable")) {
        return result;
      }

      result = result.parentElement;
    }

    return element;
  }

  async printPreview(
    sourceElement: HTMLElement,
    title: string = "Print",
    showPrintDialogImmediately: boolean = true,
    options: PopupOptions = defaultPopupOptions,
  ): Promise<boolean> {
    const initialPopupBounds = centerPopup(400, 200);
    const popupOptions = stringifyPopupOptions({...options, ...initialPopupBounds});
    const win = await this.showPrintPreviewWindow("", "", popupOptions);
    // console.log("Await done", win);

    if (!win) throw new Error("Popup blocker prevented print preview.");

    win.document.head.innerHTML = `<title>${escapeHtml(title)}</title>`;
    win.document.body.innerHTML = `<h1 style="text-align: center;">Preparing print preview</h1>`;

    const htmlElement = document.querySelector("html") as HTMLHtmlElement;
    const bodyElement = document.querySelector("body") as HTMLBodyElement;
    const originalClassName = htmlElement.className;
    htmlElement.className = `${htmlElement.className} print-preview-assist`;

    const printPreviewContainer = document.createElement("DIV");
    printPreviewContainer.className = "print-preview-container";
    bodyElement.appendChild(printPreviewContainer);

    const {html, bounds} = copyDOM(sourceElement, printPreviewContainer, true, true);

    bodyElement.removeChild(printPreviewContainer);

    htmlElement.className = originalClassName;

    win.moveTo(bounds.left, bounds.top);
    win.resizeTo(bounds.width, bounds.height);
    win.document.body.innerHTML = html;

    if (showPrintDialogImmediately) {
      win.print();
    }

    return true;
  }
}
